/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import classNames from "classnames";
import Color from "color";
import AppContext from "PFApp/app_context";
import { useDetailsPanelContext } from "PFApp/booking/components/details_panel";
import { LoadingDots } from "PFComponents/loading_dots";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { Booking } from "PFCore/types/booking";
import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchBooking } from "../../../../../services/api";
import BookingTooltipContent from "../../bars/booking_bar/booking_tooltip_content";
import css from "./heat_map_item.module.scss";
import { HeatMap } from "./heat_map_item.types";
import { HeatMapLabel } from "./heat_map_label";

type HeatMapItemProps = {
  rootClassName?: string;
  labelClassName?: string;
  shouldDisplayLabel?: boolean;
  label: string;
  bgColor: string | undefined;
} & HeatMap;

const modifiers = [
  {
    name: "preventOverflow",
    options: {
      altBoundary: true
    }
  }
];

const HeatMapItem = ({
  left,
  width,
  bookings = [],
  rootClassName,
  labelClassName,
  label,
  bgColor,
  shouldDisplayLabel
}: HeatMapItemProps): JSX.Element => {
  const {
    store: { currentAccount }
  } = useContext(AppContext);
  const { openDetailsPanel } = useDetailsPanelContext();

  const { t } = useTranslation("bookingModule");
  const [bookingsData, setBookingsData] = useState<Booking[]>([]);
  const [errorsByBookingId, setErrorsByBookingId] = useState<Record<number, string>>({});
  const { getBookingCategory } = useBookingCategories();
  const { getFirstError } = useResponseErrors();
  const style = {
    width,
    left,
    ...(bgColor && { background: bgColor })
  };

  const handleClick = useCallback(
    (event) => {
      if (!bookings.length) {
        return;
      }
      event.stopPropagation();
      if (bookings.length > 1) {
        return;
      }
      const { id, categoryId } = bookings[0];
      const category = getBookingCategory(categoryId);
      if (category && !errorsByBookingId[id]) {
        openDetailsPanel({ id, type: "booking" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookings, currentAccount, errorsByBookingId]
  );

  const handleHover = useCallback(async () => {
    if (bookings.length > 2) {
      return;
    }
    try {
      const result = await Promise.allSettled(
        bookings.map(async ({ id, categoryId }) => {
          const category = getBookingCategory(categoryId);
          if (category) {
            return fetchBooking(id);
          } else {
            return null;
          }
        })
      );
      const bookingsData: Booking[] = result
        .filter((promise) => promise.status !== "rejected")
        .map((promise) => (promise as PromiseFulfilledResult<Booking>).value);
      const errors = result.reduce((errorsById, currentPromise, index) => {
        if (currentPromise.status === "fulfilled") {
          return errorsById;
        }
        const { reason } = currentPromise as PromiseRejectedResult;
        const error = getFirstError(reason);
        return {
          ...errorsById,
          [bookings[index].id]: error
        };
      }, {});
      setBookingsData(bookingsData);
      setErrorsByBookingId(errors);
    } catch {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const multipleBookingsTooltip = (
    <BookingTooltipContent
      multiple
      className={css.tooltip}
      title={t("bookings.tooltip.multiple")}
      multipleInfoLabel={t("bookings.tooltip.multipleExpandProfile")}
    />
  );

  const tooltipContent =
    bookings.length > 2 ? (
      multipleBookingsTooltip
    ) : bookingsData?.length ? (
      bookingsData?.map((data, index) => {
        const { id, start_date: start, end_date: end, booking_category_id: categoryId, title } = data || {};
        const category = getBookingCategory(categoryId);
        const error = errorsByBookingId[bookings[index].id];
        let tooltipTitle = title;
        if (error) {
          tooltipTitle = error;
        } else if (!category) {
          tooltipTitle = t("bookings.tooltip.noCategory");
        }
        return (
          <BookingTooltipContent
            key={id}
            className={classNames(css.tooltip, {
              [css.tooltipTop]: index === 0 && bookingsData.length === 2,
              [css.tooltipBottom]: index === 1 && bookingsData.length === 2
            })}
            title={tooltipTitle}
            {...(category
              ? {
                  start,
                  end,
                  categoryName: category.display_as
                }
              : {})}
          />
        );
      })
    ) : (
      <div className={classNames(css.tooltip, css.tooltipLoading)}>
        <LoadingDots />
      </div>
    );

  const heatMapTile = (
    <div
      className={classNames(
        css.item,
        { [css.lightBgColor]: !bgColor || Color(bgColor).isLight() },
        rootClassName
      )}
      style={style}
      onClick={handleClick}
      onMouseEnter={handleHover}
    >
      {shouldDisplayLabel && <HeatMapLabel label={label} rootClassName={labelClassName} />}
    </div>
  );

  return bookings.length ? (
    <Tooltip
      content={tooltipContent}
      placement="top"
      popperOptions={{
        modifiers
      }}
      theme="pf-translucent"
    >
      {heatMapTile}
    </Tooltip>
  ) : (
    heatMapTile
  );
};

export default HeatMapItem;
